/**
 * CombatLabConfig — the `/combatlab` mode URL: one click into the measured
 * combat/VFX arena (`combat-lab`, src/game/maps/CombatLab.ts).
 *
 * Sibling to /debug + /abilitygrounds. It is a DEV MEASUREMENT surface, not a
 * game mode: the lab exists so a cone/arc/VFX reading is taken against a fixed
 * ruler instead of against pigs that move between the HP-before and HP-after
 * samples (COMBAT-MELEE-CONE-02, 2026-07-26).
 *
 * Why its own route rather than folding into /debug: `/debug` is pinned to
 * `debug-lobby` (coerceModeUrl force-sets `?map=` on every coerced path, so
 * `/debug?map=combat-lab` cannot work), and the two surfaces want opposite
 * stages — the debug lobby is deliberately dark so net overlays stay readable,
 * the lab is a flat neutral stage so VFX read and screenshots compare.
 *
 * Class is intentionally NOT injected: the whole point is walking the melee
 * class table (warrior / paladin / shaman / rogue), so the picker shows and
 * `?class=X` deep-links stay the fast path for probes.
 *
 * Wiring:
 *   - applyModeUrlCoercion.ts → coerceModeUrl({ path: '/combatlab', mapId: 'combat-lab' })
 *   - main.tsx                → <Route path="/combatlab" />
 *   - HomeModePicker.tsx      → sandbox card
 *   - LevelRegistry.ts        → registerLevel(CombatLab)
 */

import type { ModeUrlConfig } from '../boot/coerceModeUrl';

export const COMBAT_LAB_CONFIG: ModeUrlConfig = {
  resetOnLoad: true,
  // Own preset (systems identical to `debug-tools-only`) so the lab can carry
  // its own PRESENTATION profile without changing /debug: multiplayer coord
  // labels off, HP-bar numbers off, nameplates (= the ruler readings) on.
  // 2026-07-26 — with the shared preset, 13 dummies each rendered a 6-line
  // diagnostics dump and the stage was unusable for the VFX observation this
  // map exists to support. `?diag=1` still forces the labels back on.
  preset: 'combat-lab',
  skipSplash: true,
  skipLogin: true,
  // Render-truth hooks (__h2kRenderState / __h2kCast) — the probe scenarios in
  // scripts/mcp/probe-scenarios/melee-cone-table.json drive them directly.
  testhooks: true,
  // The quick class switcher stays OFF here, like everywhere else — DJ retired
  // every opt-in on 2026-07-10 (stale class roster). Walk the melee table with
  // `?class=X` deep-links, which is what the probe scenarios do anyway.
};
